import type { Maybe } from '@adornis/base/utilTypes.js';
import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import { EmptyState } from '@adornis/components/client/EmptyState.js';
import { confirmDialog, guardDialog, waitForDialog } from '@adornis/dialog/dialog-functions.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { ANY_CONTEXT, GLOBAL_CONTEXT } from '@adornis/users/db/a-roles.js';
import { impersonateUser } from '@adornis/users/db/a-user.js';
import type { BaseError } from '@adornis/validation/errors/BaseError.js';
import { ActionGrid, type DashboardAction } from '@martinus/action-grid/client/ActionGrid.js';
import { html } from 'lit';
import { PersonTab } from '../db/enums/misc-enums.js';
import { Permission, Role } from '../permissions/roles.js';
import { UserAPI } from '../users/api/UserAPI.js';
import { personDetailRouter } from '../users/details/client/UserDetails.js';
import { Person } from '../users/entities/person.js';
import { UserListSelectionInterface } from '../users/list/entities/UserListSelectionInterface.js';
import { displayMailError } from './client-utils.js';
import { SgSnackbar } from './components/ui/sg-snackbar.js';

const actions: DashboardAction<Person>[] = [
  {
    name: 'Verträge / Änderungsanzeige',
    icon: 'icon-[material-symbols--contract-rounded]',
    color: 'bg-primary text-primary-content',
    show: (user: Maybe<Person>, person: Maybe<Person>) =>
      user?.hasPermission(Permission.AG_SEKTION_W) && !!person?.roles.find(r => r.name === Role.MITARBEITER),
    action: (user: Maybe<Person>, person: Maybe<Person>) => goTo(`personen/${person?._id}/mitarbeiter/vertraege`),
  },
  {
    name: 'Einstellungsanzeige',
    icon: 'icon-[material-symbols--contract-rounded]',
    color: 'bg-success text-success-content',
    show: (user: Maybe<Person>, person: Maybe<Person>) =>
      user?.hasPermission(Permission.AG_SEKTION_W) &&
      !person?.roles.find(r => r.name === Role.MITARBEITER) &&
      !!person?.roles.find(r => r.name === Role.KANDIDAT),
    action: (user: Maybe<Person>, person: Maybe<Person>) =>
      goTo(`personen/${person?._id}/mitarbeiter/vertraege?createNew=true`),
  },
  {
    name: 'MAV-Zustimmung',
    icon: 'icon-[material-symbols--handshake-rounded]',
    color: 'bg-secondary text-secondary-content',
    show: (user: Maybe<Person>, person: Maybe<Person>) =>
      !!user?.roles.find(r => r.name === Role.MAV) && user._id !== person?._id,
    action: (user: Maybe<Person>, person: Maybe<Person>) => goTo(`personen/${person?._id}/mitarbeiter/vertraege`),
  },
  {
    name: 'Impersonieren',
    icon: 'icon-[material-symbols--group-rounded]',
    color: 'bg-warning text-warning-content',
    show: (user: Maybe<Person>, person: Maybe<Person>) => user?.hasPermission(Permission.IMPERSONATE, GLOBAL_CONTEXT),
    action: (user: Maybe<Person>, person: Maybe<Person>) =>
      guardDialog(() => impersonateUser(person?._id ?? '', Person._class)(), {
        success: () => {
          void SgSnackbar.show('Benutzer wurde impersoniert');
          goTo('/');
          location.reload();
        },
      }),
  },
  {
    name: 'Einladungslink erneut versenden',
    icon: 'icon-[material-symbols--contact-mail-rounded]',
    color: 'bg-info text-info-content',
    // the permission resendInitMail checks on the server
    show: (user: Maybe<Person>, person: Maybe<Person>) => user?.hasPermission(Permission.RESEND_INVITE),
    action: async (user: Maybe<Person>, person: Maybe<Person>) => {
      const confirm = await confirmDialog('Einladungslink wirklich erneut versenden?');
      if (!confirm) return;

      try {
        await waitForDialog(UserAPI.resendInitMail(person?._id ?? '')(), 'Einladungsemail wird erneut versendet..');
        void SgSnackbar.show('Einladungslink wurde erfolgreicht versendet');
      } catch (e) {
        displayMailError(e as BaseError);
      }
    },
  },
  {
    name: 'Buchungen bearbeiten',
    icon: 'icon-[material-symbols--group-add-rounded]',
    color: 'bg-accent text-accent-content',
    show: (user: Maybe<Person>, person: Maybe<Person>) => user?.hasPermission(Permission.EDIT_BOOKINGS, ANY_CONTEXT),
    action: async (user: Maybe<Person>, person: Maybe<Person>) => {
      const dataInterface = new UserListSelectionInterface({
        userIDs: [person?._id],
      });

      const selectionString = JSON.stringify(dataInterface);

      goTo(`/bookings?selection=${selectionString}`);
    },
  },

  {
    name: 'Lastschriftmandate',
    icon: 'icon-[material-symbols--payments-rounded]',
    color: 'bg-accent text-accent-content',
    show: (user: Maybe<Person>, person: Maybe<Person>) =>
      user?._id === person?._id || user?.roles.some(r => r.name === Role.BUCHHALTUNG || r.name === Role.ADMIN),
    action: (_user: Maybe<Person>, person: Maybe<Person>) => goTo(`personen/${person?._id}/sepa`),
  },
];

export const PersonDashboard = stateful((props: { personID: Maybe<string> }) => {
  const { value: person, loading } = useQuery(
    () => subscribeByID(Person, props.personID)({ _id: 1, roles: { name: 1 } }),
    [props.personID],
  );

  if (loading)
    return html`<div class="flex justify-center p-8"><span class="loading loading-spinner loading-lg"></span></div>`;

  if (!person)
    return EmptyState({ title: 'Person nicht gefunden', icon: 'icon-[material-symbols--person-off-rounded]' });

  return html`
    <div class="card-container">
      ${ActionGrid({
        actions,
        args: [person],
        onEmpty: () => personDetailRouter.goTo(PersonTab.PROFIL, { replaceState: true }),
      })}
    </div>
  `;
});
